import React from 'react';
import { Link } from 'react-router-dom';
import { TFunction } from 'i18next';
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { Archive, Briefcase, Eye, Users } from 'lucide-react';
import { ADMIN_PAGES_KEYS } from '../../../../i18n/constants/keys';
import { IconWithLabel } from '../../../../ui';
import { IColumn, IJobOpeningRow } from '../interfaces';
import { TooltipWrapper, unixToCurrentTimezone } from './jobOpenings.functions';

export const getJobOpeningsColumns = (
  t: TFunction,
  onRowClick?: (id: string) => void,
  isClosed: boolean = false,
): IColumn<IJobOpeningRow>[] => [
  {
    key: 'jobTitle',
    label: t(ADMIN_PAGES_KEYS.jobOpenings.list.table.jobTitle),
    width: '260px',
    sortable: true,
    align: 'left',
    render: (row) => (
      <IconWithLabel
        label={row.jobTitle || '-'}
        icon={isClosed ? <Archive size={16} /> : <Briefcase size={16} />}
        color={isClosed ? 'error' : 'primary'}
        to={`/job-openings/${row.id}`}
        onClick={onRowClick ? () => onRowClick(row.id) : undefined}
        maxWidth={220}
      />
    ),
  },
  {
    key: 'jobReference',
    label: t(ADMIN_PAGES_KEYS.jobOpenings.list.table.jobReference),
    width: '180px',
    sortable: false,
    align: 'left',
    render: (row) => (
      <Chip
        label={row.jobReference || '-'}
        size="small"
        variant="outlined"
        sx={{
          fontWeight: 500,
          fontSize: '0.75rem',
          bgcolor: 'grey.50',
          borderColor: 'grey.300',
          color: 'text.secondary',
        }}
      />
    ),
  },
  {
    key: 'hiringManager',
    label: t(ADMIN_PAGES_KEYS.jobOpenings.list.table.hiringManager),
    width: '180px',
    sortable: false,
    align: 'left',
    render: (row) => (
      <TooltipWrapper text={Array.isArray(row.hiringManager) ? row.hiringManager.join(', ') : '-'} />
    ),
  },
  {
    key: 'internalRecruiter',
    label: t(ADMIN_PAGES_KEYS.jobOpenings.list.table.internalRecruiter),
    width: '180px',
    sortable: false,
    align: 'left',
    render: (row) => (
      <TooltipWrapper text={Array.isArray(row.internalRecruiter) ? row.internalRecruiter.join(', ') : '-'} />
    ),
  },
  {
    key: 'applicants',
    label: t(ADMIN_PAGES_KEYS.jobOpenings.list.table.applicants),
    width: '120px',
    sortable: false,
    align: 'left',
    render: (row) => (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.5,
          py: 0.5,
          borderRadius: 2,
          bgcolor: 'primary.50',
          color: 'primary.dark',
          fontWeight: 600,
          fontSize: '0.8125rem',
        }}
      >
        <Users size={14} className="text-primary-600" />
        <span>{row.applicants ?? 0}</span>
      </Box>
    ),
  },
  {
    key: 'creationDate',
    label: t(ADMIN_PAGES_KEYS.jobOpenings.list.table.creationDate),
    width: '150px',
    sortable: true,
    align: 'left',
    render: (row) => (
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
        {unixToCurrentTimezone(row.creationDate, false)}
      </Typography>
    ),
  },
  {
    key: 'expiryDate',
    label: t(ADMIN_PAGES_KEYS.jobOpenings.list.table.expiryDate),
    width: '150px',
    sortable: true,
    align: 'left',
    render: (row) => (
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
        {unixToCurrentTimezone(row.expiryDate, false)}
      </Typography>
    ),
  },
  {
    key: 'tags',
    label: t(ADMIN_PAGES_KEYS.jobOpenings.list.table.tags),
    width: '220px',
    sortable: false,
    align: 'left',
    render: (row) => {
      const tags = row.tags || [];
      if (tags.length === 0) {
        return (
          <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
            {t(ADMIN_PAGES_KEYS.jobOpenings.list.table.noTags)}
          </Typography>
        );
      }

      const visibleTags = tags.slice(0, 2);
      const remainingTags = tags.slice(2);

      return (
        <Stack direction="row" spacing={0.5} alignItems="center">
          {visibleTags.map((tag, idx) => (
            <Chip
              key={idx}
              label={tag}
              size="small"
              sx={{
                fontSize: '0.7rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                bgcolor: 'emerald.50',
                color: 'success.dark',
                borderColor: 'success.light',
                height: 22,
              }}
            />
          ))}
          {remainingTags.length > 0 && (
            <Tooltip
              title={
                <Stack spacing={0.5}>
                  {remainingTags.map((tag, i) => (
                    <Typography key={i} variant="caption" color="inherit">
                      {tag}
                    </Typography>
                  ))}
                </Stack>
              }
              arrow
            >
              <Chip
                label={`+${remainingTags.length}`}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  bgcolor: 'grey.100',
                  color: 'text.secondary',
                  height: 22,
                  cursor: 'pointer',
                }}
              />
            </Tooltip>
          )}
        </Stack>
      );
    },
  },
  {
    key: 'actions',
    label: '',
    width: '60px',
    sortable: false,
    align: 'center',
    render: (row) => (
      <Tooltip title="Public Preview" arrow>
        <IconButton
          component={Link}
          to={`/jobs/${row.id}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          size="small"
          aria-label="Public Preview"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              color: 'primary.main',
              bgcolor: 'primary.50',
            },
          }}
        >
          <Eye size={16} />
        </IconButton>
      </Tooltip>
    ),
  },
];
